import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getProperties } from '../api/api';
import { BedDouble, Users, MapPin } from 'lucide-react';

const PropertiesPage = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const data = await getProperties();
        setProperties(data);
      } catch (error) {
        console.error('Error loading properties:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <Navbar />

      {/* Header */}
      <section className="text-white py-20" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f4c75 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block mb-4">
            <span className="badge-premium">Premium Collection</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="properties-page-title" style={{ letterSpacing: '-0.02em' }}>
            Our Properties
          </h1>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: '#e8e8e8' }}>
            Browse our selection of premium serviced accommodation across the North West
          </p>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-lg" style={{ color: '#636e72' }}>Loading properties...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property) => (
                <Link key={property.id} to={`/properties/${property.id}`}>
                  <Card className="property-card h-full rounded-2xl" data-testid={`property-card-${property.id}`}>
                    <div className="h-64 overflow-hidden relative">
                      <img 
                        src={property.images[0]} 
                        alt={property.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center text-sm mb-2" style={{ color: '#d4a574' }}>
                        <MapPin size={16} className="mr-1" />
                        {property.location}
                      </div>
                      <h3 className="text-2xl font-bold mb-3" style={{ color: '#1a1a2e' }}>{property.name}</h3>
                      <p className="mb-4 line-clamp-2" style={{ color: '#636e72' }}>{property.description}</p>
                      <div className="flex items-center space-x-4 text-sm mb-4" style={{ color: '#636e72' }}>
                        <div className="flex items-center">
                          <BedDouble size={16} className="mr-1" />
                          {property.bedrooms} bedrooms ({property.beds} beds)
                        </div>
                        <div className="flex items-center">
                          <Users size={16} className="mr-1" />
                          {property.guests} guests
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {property.amenities.slice(0, 3).map((amenity, index) => (
                          <span key={index} className="amenity-badge">
                            {amenity}
                          </span>
                        ))}
                      </div>
                      <Button data-testid={`view-details-${property.id}`} className="w-full btn-primary-custom rounded-full">
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f4c75 100%)' }}>
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Can't Find What You're Looking For?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ color: '#e8e8e8' }}>Get in touch and we'll help you find the perfect property for your needs.</p>
          <Link to="/enquiry">
            <Button data-testid="properties-enquire-button" size="lg" className="btn-primary-custom px-10 py-6 text-lg rounded-full">
              Make an Enquiry
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PropertiesPage;